import {
  ALLOWED_IMAGE_MIME_TYPES,
  ALLOWED_VIDEO_MIME_TYPES,
} from "@coinfallsmp/shared";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUploadsEnabled } from "../AppConfigContext.js";
import { canUploadNow, creatorOf, useAuth } from "../AuthContext.js";
import {
  createVideo,
  finalizeUpload,
  getThumbnailUploadUrl,
  getUploadUrl,
  readVideoMetadata,
  uploadToPresignedUrl,
} from "../api.js";

type Stage =
  | "idle"
  | "creating"
  | "uploading-video"
  | "uploading-thumbnail"
  | "finalizing"
  | "done";

export default function NewUpload() {
  const navigate = useNavigate();
  const { identity, loading } = useAuth();
  const uploadsEnabled = useUploadsEnabled();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState<Stage>("idle");
  const [error, setError] = useState<string | null>(null);

  const busy = stage !== "idle" && stage !== "done";

  if (loading) return <p className="status-message">Loading…</p>;
  if (!creatorOf(identity)) {
    return (
      <section className="form-page">
        <h1>Become a CoinfallSMP Creator</h1>
        <p className="form-hint">
          You need a creator account to upload videos.
        </p>
        <div className="button-row">
          <Link to="/login" className="button secondary">
            Log in
          </Link>
          <Link to="/apply" className="button primary">
            Apply for Creator
          </Link>
        </div>
      </section>
    );
  }
  if (!canUploadNow(identity)) {
    return (
      <section className="form-page">
        <h1>Upload a video</h1>
        <p className="status-message">
          Upload permission hasn't been granted for your account yet — an admin needs to
          approve your creator account and enable uploads first.
        </p>
      </section>
    );
  }
  if (!uploadsEnabled) {
    return (
      <section className="form-page">
        <h1>Upload a video</h1>
        <p className="status-message">
          Direct file uploads aren't available on this deployment yet — R2 storage isn't
          configured. In the meantime, use <Link to="/import">Import from YouTube</Link> to
          add a video by embedding its existing YouTube link instead.
        </p>
      </section>
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) {
      setError("Choose a video file first.");
      return;
    }
    if (!ALLOWED_VIDEO_MIME_TYPES.includes(file.type as never)) {
      setError(`Unsupported video type: ${file.type || "unknown"}`);
      return;
    }

    setError(null);
    try {
      setStage("creating");
      const video = await createVideo({
        title: title.trim() || file.name,
        description: description.trim() || undefined,
      });

      const metadata = await readVideoMetadata(file).catch(() => null);

      setStage("uploading-video");
      const { uploadUrl, key } = await getUploadUrl(video.id, {
        contentType: file.type as (typeof ALLOWED_VIDEO_MIME_TYPES)[number],
        fileSizeBytes: file.size,
      });
      await uploadToPresignedUrl(uploadUrl, file, setProgress);

      setStage("finalizing");
      await finalizeUpload(video.id, {
        kind: "video",
        key,
        durationSeconds: metadata?.durationSeconds,
        width: metadata?.width,
        height: metadata?.height,
      });

      if (thumbnail) {
        setStage("uploading-thumbnail");
        const thumb = await getThumbnailUploadUrl(video.id, {
          contentType: thumbnail.type as (typeof ALLOWED_IMAGE_MIME_TYPES)[number],
          fileSizeBytes: thumbnail.size,
        });
        await uploadToPresignedUrl(thumb.uploadUrl, thumbnail);
        await finalizeUpload(video.id, { kind: "thumbnail", key: thumb.key });
      }

      setStage("done");
      navigate(`/videos/${video.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
      setStage("idle");
    }
  }

  return (
    <section className="form-page">
      <h1>Upload a video</h1>
      <form onSubmit={onSubmit}>
        <label>
          Title
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Session 42 — the great collapse"
            disabled={busy}
          />
        </label>

        <label>
          Description
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            disabled={busy}
          />
        </label>

        <label>
          Video file
          <input
            type="file"
            accept={ALLOWED_VIDEO_MIME_TYPES.join(",")}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={busy}
          />
        </label>

        <label>
          Thumbnail (optional)
          <input
            type="file"
            accept={ALLOWED_IMAGE_MIME_TYPES.join(",")}
            onChange={(e) => setThumbnail(e.target.files?.[0] ?? null)}
            disabled={busy}
          />
        </label>

        {stage === "uploading-video" ? (
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
        ) : null}

        {error ? <p className="form-error">{error}</p> : null}

        <button type="submit" className="button primary" disabled={busy}>
          {stageLabel(stage)}
        </button>
      </form>
    </section>
  );
}

function stageLabel(stage: Stage): string {
  switch (stage) {
    case "creating":
      return "Creating…";
    case "uploading-video":
      return "Uploading video…";
    case "uploading-thumbnail":
      return "Uploading thumbnail…";
    case "finalizing":
      return "Finishing up…";
    case "done":
      return "Done";
    default:
      return "Upload";
  }
}
